import { HedgeList } from '@/components/hedge-list'
import type { ExpiresFilter, SortOption, StatusFilter } from '@/components/hedges'
import { Button } from '@/components/ui/button'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { Plus } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

// Define search params schema - no defaults, allow undefined
const hedgesSearchSchema = z.object({
  from: z.array(z.string()).optional(),
  to: z.array(z.string()).optional(),
  status: z.enum(['all', 'active', 'expired']).optional(),
  expires: z.enum(['all', '7', '30', '90']).optional(),
  sort: z.enum(['expiringSoon', 'expiringLast']).optional(),
})

export type HedgesSearchParams = {
  from: string[]
  to: string[]
  status: StatusFilter
  expires: ExpiresFilter
  sort: SortOption
}

export const Route = createFileRoute('/_layout/hedges/')({
  component: HedgesIndex,
  validateSearch: hedgesSearchSchema,
})

// Default values for filters
const DEFAULT_FILTERS: HedgesSearchParams = {
  from: [],
  to: [],
  status: 'all',
  expires: 'all',
  sort: 'expiringSoon',
}

function HedgesIndex() {
  const { t } = useTranslation()
  const search = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })

  // Apply defaults for missing values
  const filters: HedgesSearchParams = {
    from: search.from ?? DEFAULT_FILTERS.from,
    to: search.to ?? DEFAULT_FILTERS.to,
    status: search.status ?? DEFAULT_FILTERS.status,
    expires: search.expires ?? DEFAULT_FILTERS.expires,
    sort: search.sort ?? DEFAULT_FILTERS.sort,
  }

  // Update URL when filters change - omit defaults for cleaner URLs
  const updateFilters = (updates: Partial<HedgesSearchParams>) => {
    const newFilters = { ...filters, ...updates }

    // Build search params, omitting values that match defaults
    const search: Record<string, unknown> = {}

    if (newFilters.from.length > 0) search.from = newFilters.from
    if (newFilters.to.length > 0) search.to = newFilters.to
    if (newFilters.status !== 'all') search.status = newFilters.status
    if (newFilters.expires !== 'all') search.expires = newFilters.expires
    if (newFilters.sort !== 'expiringSoon') search.sort = newFilters.sort

    navigate({
      search,
      replace: true,
    })
  }

  return (
    <div className="space-y-6">
      {/* Header - only shown on list view */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">{t('hedges.title')}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t('hedges.subtitle')}</p>
        </div>
        <Button asChild>
          <Link to="/hedges/new">
            <Plus className="w-4 h-4 mr-2" />
            {t('hedges.new')}
          </Link>
        </Button>
      </div>

      <HedgeList filters={filters} onFiltersChange={updateFilters} />
    </div>
  )
}
